// pages/_app.js
import { setFeatureFlagUserProperties } from '@/providers/amplitude-feature-flag-properties';
import { posthogFlagsReadyAtom } from '@/providers/posthog-flags';
import {
  buildPosthogInitOptions,
  getLaunchKindeUserId,
  isEhrLaunchRoute,
} from '@/providers/posthog-init-options';
import { isTauri } from '@/tauri/utils';
import { AppType } from '@/types';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { getBuildIdTraits } from '@/utils/appVersion';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { deferTelemetryInit } from '@/utils/deferredTelemetry';
import { logger } from '@/utils/logger/logger';
import { registerAnalyticsAdapter } from '@heidi/analytics';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { memo, useEffect } from 'react';

const getAppType = (): AppType => {
  if (isTauri()) {
    return AppType.Desktop;
  }
  return AppType.Web;
};

// APP-6116 (§5.11): build_id rides as a PostHog super-property via register()
// below — NOT a person property (setPersonPropertiesForFlags), so a web↔desktop
// identity can't clobber it. As a super-property it tags every event, including
// autocapture / $pageview / session replay that never go through `trackEventV2`.
// `getBuildIdTraits()` omits it on web / local builds; see that helper in
// @/utils/appVersion for the shared omit-when-absent convention.

function initFeatureFlags(): void {
  // Re-register super properties here (not just at module load): posthog.reset()
  // during logout clears all super properties, so we need to re-establish them
  // alongside the post-logout reload of feature-flag person properties.
  posthog.register({
    App: getAppType(),
    installer_type: getDesktopInstallerType(),
    ...getBuildIdTraits(),
  });
  posthog.setPersonPropertiesForFlags({
    App: getAppType(),
    installer_type: getDesktopInstallerType(),
  });
  posthog.reloadFeatureFlags();
}

// TELEMETRY-GATE: F1 module-load gate. Canonical pattern referenced from
// .ai-context/onprem-guardrails.md — bundlers DCE the unreachable branch when
// telemetry is suppressed, so PostHog never ships nor phones home.
// Check that PostHog is client-side (used to handle Next.js SSR).
if (typeof window !== 'undefined' && isSaasTelemetryEnabled) {
  // Surveys extension is required because `disable_external_dependency_loading`
  // (below) strips PostHog's runtime loader — without this preload,
  // `window.__PosthogExtensions__.generateSurveys` is never populated and
  // PostHog logs "loadExternalDependency extension not found" when a survey
  // tries to render. Dynamic-imported so the ~92 KB bundle splits out of the
  // _app critical chunk, and deferred to post-load idle so its
  // fetch + eval don't compete with hydration — surveys never render at first
  // paint.
  //
  // loadIfEnabled() after the chunk lands covers both orderings: posthog's
  // internal onFeatureFlags chain usually fires before the idle-deferred chunk
  // registers `generateSurveys` (in which case this call renders any pending
  // survey), and if flags resolve later posthog's own chain finds the
  // extension already registered. Optional chaining covers the surveys
  // instance being TreeShakeable<PostHogSurveys>.
  deferTelemetryInit(() => {
    void import('posthog-js/dist/surveys')
      .then(() => posthog.surveys?.loadIfEnabled?.())
      .catch((error) => {
        logger.warn('[PosthogProvider] failed to load PostHog surveys extension', {
          error: String(error),
        });
      });
  });

  // posthog.init itself stays SYNCHRONOUS: PostHog drives feature
  // flags that gate first-paint UI (ThemeManager, RTL, boot-gated variants) —
  // deferring it would delay flag resolution and flash wrong UI.
  const pathname = window.location.pathname;
  posthog.init(
    process.env.NEXT_PUBLIC_POSTHOG_KEY || '',
    buildPosthogInitOptions(isTauri(), pathname)
  );
  posthog.register({
    App: getAppType(),
    installer_type: getDesktopInstallerType(),
    ...getBuildIdTraits(),
  });
  const launchKindeUserId = getLaunchKindeUserId(window.location.search, pathname);
  if (launchKindeUserId) {
    // reload=false: the reload identify() queues already carries these.
    posthog.setPersonPropertiesForFlags(
      { App: getAppType(), installer_type: getDesktopInstallerType() },
      false
    );
    posthog.identify(launchKindeUserId);
  }
  if (isEhrLaunchRoute(pathname)) {
    logger.info('[PosthogProvider] EHR launch identity at init', {
      identifiedAtInit: Boolean(launchKindeUserId),
      ehrProvider: new URLSearchParams(window.location.search).get('ehr_provider'),
    });
  }

  // P2 [3/5]: PostHog transport binding for the @heidi/analytics
  // pipeline — registered inside this TELEMETRY-GATE so a suppressed build
  // registers nothing (F5: absence, not a runtime check). Fires only for
  // events routed with `options.posthog` (pipeline default is OFF).
  registerAnalyticsAdapter({
    destination: 'posthog',
    enabled: true,
    send: (payload) => {
      posthog.capture(payload.event, payload.props);
    },
  });
}

const jotaiStore = getDefaultStore();

type PosthogProviderProps = {
  children: React.ReactNode;
};
const PosthogProvider = memo(({ children }: PosthogProviderProps) => {
  const router = useRouter();
  const flagsReady = useAtomValue(posthogFlagsReadyAtom);

  // Re-fires after invalidatePosthogFlags() resets flagsReady during logout,
  // re-fetching flags for the now-anonymous user before the login page renders.
  useEffect(() => {
    // TELEMETRY-GATE: PostHog never inits when telemetry is off, so nothing
    // must ever await its readiness (design D6).
    if (!isSaasTelemetryEnabled) return;
    if (flagsReady) return;

    logger.debug('[PosthogProvider] waiting for feature flags...');
    initFeatureFlags();

    const unsubscribe = posthog.onFeatureFlags((flags) => {
      logger.debug('[PosthogProvider] onFeatureFlags fired, flags received:', flags);
      setFeatureFlagUserProperties(posthog.featureFlags.getFlagVariants());
      jotaiStore.set(posthogFlagsReadyAtom, true);
    });

    const timeout = setTimeout(() => {
      logger.warn('[PosthogProvider] feature flags timed out after 5s, rendering anyway');
      jotaiStore.set(posthogFlagsReadyAtom, true);
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [flagsReady]);

  useEffect(() => {
    // TELEMETRY-GATE: no $pageview emission when telemetry is suppressed.
    if (!isSaasTelemetryEnabled) return;
    // Track page views
    const handleRouteChange = () => posthog?.capture('$pageview');
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
});

PosthogProvider.displayName = 'PosthogProvider';

export default PosthogProvider;
